// src/components/layout/Sidebar.js
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Campaign as CampaignIcon,
  Description as DescriptionIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Build as BuildIcon,
  Feedback as FeedbackIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../hooks/useAuth';
import { designTokens } from '../../theme/designTokens';
import FeedbackDialog from '../FeedbackDialog';
import titleIcon from '../../assets/images/title.png';

const drawerWidth = 220;
const collapsedDrawerWidth = 60;

// 機能別アイコンカラーのマッピング
const featureColors = {
  '/plans': designTokens.featureThemes.recruit_plan.primary,
  '/media': designTokens.featureThemes.media_selection.primary,
  '/job-posts': designTokens.featureThemes.job_posting.primary,
  '/interview-support': designTokens.featureThemes.interview_support.primary,
};

function Sidebar({ open, handleDrawerToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  // 会社名を取得
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/basic-info/`, {
          withCredentials: true,
        });
        setCompanyName(response.data.company_name || '');
      } catch (error) {
        console.error("Failed to fetch company name:", error);
        if (error.response?.status === 401) {
          console.log('401 error handled by axios interceptor');
        }
        setCompanyName('');
      }
    };
    fetchCompanyName();
  }, []);

  // イニシャル計算
  const initials = companyName
    ? companyName
        .split(' ')
        .map(part => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'DJ';

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();

      await axios.post(`${process.env.REACT_APP_API_URL}/auth/logout`, {}, {
        withCredentials: true,
      });

      console.log("ログアウトが正常に完了しました");
    } catch (error) {
      console.error("ログアウト時にエラーが発生しました:", error);
    } finally {
      window.location.replace('/login');
    }
  };

  // ユーザーロールに応じてメニュー項目を制御
  const getMenuSections = () => {
    // interview_onlyロールの場合
    if (user?.role === 'interview_only') {
      return [
        {
          title: '採用業務',
          items: [
            { text: '面接サポート', icon: <QuestionAnswerIcon />, path: '/interview-support' }
          ]
        }
      ];
    }

    // general, admin ロールの場合
    const sections = [
      {
        title: 'ワークスペース',
        items: [
          { text: 'ダッシュボード', icon: <DashboardIcon />, path: '/home' }
        ]
      },
      {
        title: '採用業務',
        items: [
          { text: '採用計画アシスト', icon: <AssignmentIcon />, path: '/plans' },
          { text: '募集媒体選定', icon: <CampaignIcon />, path: '/media' },
          { text: '求人票の自動作成', icon: <DescriptionIcon />, path: '/job-posts' },
          { text: '面接サポート', icon: <QuestionAnswerIcon />, path: '/interview-support' }
        ]
      },
      {
        title: 'システム',
        items: [
          { text: '設定', icon: <SettingsIcon />, path: '/settings' }
        ]
      }
    ];

    // 管理者ロールを持つユーザーにのみ管理コンソールを表示
    if (user?.role === 'admin') {
      sections[2].items.push({ text: '管理コンソール', icon: <BuildIcon />, path: '/admin' });
    }

    return sections;
  };

  const menuSections = getMenuSections();

  // 現在のパスと比較し、アクティブメニューを判定
  const isActive = (path) => location.pathname === path;

  // アイコンの色を取得（機能別カラーまたはデフォルト）
  const getIconColor = (path, active) => {
    if (active) {
      return featureColors[path] || theme.palette.primary.main;
    }
    return alpha(theme.palette.text.primary, 0.6);
  };

  // アクティブ時の背景色を取得
  const getActiveBgColor = (path) => {
    const color = featureColors[path] || theme.palette.primary.main;
    return alpha(color, 0.12);
  };

  // プロフィールメニュー
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleSettingsClick = () => {
    handleProfileClose();
    navigate('/settings');
  };

  return (
    <Drawer
      variant={isSmDown ? 'temporary' : 'permanent'}
      open={isSmDown ? open : true}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
        BackdropProps: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: open ? drawerWidth : collapsedDrawerWidth,
          backgroundColor: theme.palette.background.paper,
          borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : 'none',
          boxShadow: 'none',
          overflowX: 'hidden',
          flexShrink: 0,
          top: 0,
          height: '100vh',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        },
      }}
    >
      {/* ロゴセクション（展開時のみ表示） / 折りたたみトグル */}
      {open ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            minHeight: 64,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box
            component={Link}
            to="/home"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              overflow: 'hidden',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.7 },
            }}
          >
            <Box
              component="img"
              src={titleIcon}
              alt="AI人事 アイコン"
              sx={{
                height: '3.5rem',
                width: 'auto',
                maxWidth: '180px',
                objectFit: 'contain',
              }}
            />
          </Box>
          {!isSmDown && (
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                color: theme.palette.text.primary,
                transition: 'color 0.2s ease, opacity 0.2s ease',
                '&:hover': {
                  color: theme.palette.primary.main,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 1.5,
            minHeight: 48,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          {!isSmDown && (
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                color: theme.palette.text.primary,
                transition: 'color 0.2s ease, opacity 0.2s ease',
                '&:hover': {
                  color: theme.palette.primary.main,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* メニューセクション */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, pt: 1 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: 1 }}>
            {/* セクションタイトル */}
            {open && (
              <Typography
                variant="caption"
                sx={{
                  px: 3,
                  py: 1,
                  display: 'block',
                  color: alpha(theme.palette.text.primary, 0.5),
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                {section.title}
              </Typography>
            )}

            {!open && sectionIndex > 0 && (
              <Divider sx={{ mx: 1.5, my: 1 }} />
            )}

            {/* メニュー項目 */}
            <List sx={{ p: 0, px: 1 }}>
              {section.items.map((item) => {
                const active = isActive(item.path);
                const iconColor = getIconColor(item.path, active);
                const bgColor = active ? getActiveBgColor(item.path) : 'transparent';
                return (
                  <Tooltip
                    title={open ? '' : item.text}
                    placement="right"
                    key={item.text}
                  >
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      sx={{
                        mt: 0.5,
                        borderRadius: 2,
                        mx: 0.5,
                        height: 44,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: theme.transitions.create([
                          'background-color',
                          'transform',
                          'box-shadow'
                        ], {
                          duration: theme.transitions.duration.shorter,
                        }),
                        backgroundColor: bgColor,
                        '&:hover': {
                          backgroundColor: active
                            ? bgColor
                            : alpha(theme.palette.action.hover, 0.06),
                          transform: 'translateX(4px)',
                          boxShadow: active ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
                        },
                        '&:active': {
                          transform: 'translateX(2px)',
                        },
                        justifyContent: open ? 'flex-start' : 'center',
                        px: open ? 2 : 1,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 2 : 'auto',
                          justifyContent: 'center',
                          color: iconColor,
                          transition: theme.transitions.create(['color'], {
                            duration: theme.transitions.duration.shorter,
                          }),
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>

                      {open && (
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: active ? 600 : 500,
                                transition: theme.transitions.create('color'),
                                color: active
                                  ? iconColor
                                  : alpha(theme.palette.text.primary, 0.85),
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.text}
                            </Typography>
                          }
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* フィードバックボタン */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <Tooltip title={open ? '' : 'フィードバック'} placement="right">
          <ListItemButton
            onClick={() => setFeedbackOpen(true)}
            sx={{
              borderRadius: 2,
              transition: theme.transitions.create([
                'background-color',
                'transform'
              ]),
              backgroundColor: 'transparent',
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: 'translateX(4px)',
              },
              '&:active': {
                transform: 'translateX(2px)',
              },
              justifyContent: open ? 'flex-start' : 'center',
              px: open ? 2 : 1,
              height: 44,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: theme.palette.primary.main,
              }}
            >
              <FeedbackIcon />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    フィードバック
                  </Typography>
                }
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* 会社プロフィールセクション */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Tooltip title={open ? '' : (companyName || 'アカウント')} placement="right">
          <Box
            onClick={handleProfileClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              px: open ? 1.5 : 0,
              py: 1,
              justifyContent: open ? 'flex-start' : 'center',
              transition: theme.transitions.create(['background-color']),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.primary.main,
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              {initials}
            </Avatar>
            {open && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {companyName || 'ログイン中...'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: alpha(theme.palette.text.primary, 0.5),
                  }}
                >
                  会社アカウント
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* プロフィールメニュー */}
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileClose}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                ml: 1,
                minWidth: 180,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <MenuItem onClick={handleSettingsClick} sx={{ py: 1.5, gap: 1.5 }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>設定</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* ログアウトボタン */}
      <Box
        sx={{
          px: 1.5,
          pb: 1.5,
        }}
      >
        <Tooltip title={open ? '' : 'ログアウト'} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              transition: theme.transitions.create([
                'background-color',
                'transform'
              ]),
              backgroundColor: 'transparent',
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                transform: 'translateX(4px)',
              },
              '&:active': {
                transform: 'translateX(2px)',
              },
              justifyContent: open ? 'flex-start' : 'center',
              px: open ? 2 : 1,
              height: 44,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: theme.palette.error.main,
                transition: theme.transitions.create('transform'),
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: theme.palette.error.main,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    ログアウト
                  </Typography>
                }
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
